import React from 'react';
import styled from 'styled-components';

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  white-space: nowrap;
  border-radius: ${props => {
    if (props.size === 'lg' || props.size === 'xl') return props.theme.borderRadius.full;
    return props.theme.borderRadius.md;
  }};
  font-size: ${props => {
    if (props.size === 'xl') return props.theme.typography.fontSize.lg;
    if (props.size === 'lg') return props.theme.typography.fontSize.base;
    return props.theme.typography.fontSize.sm;
  }};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: ${props => props.theme.transitions.default};
  cursor: pointer;
  border: none;
  outline: none;
  
  /* Scale font size on larger screens */
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => {
      if (props.size === 'xl') return props.theme.typography.fontSize.xl;
      if (props.size === 'lg') return props.theme.typography.fontSize.lg;
      return props.theme.typography.fontSize.base;
    }};
  }
  
  @media (min-width: ${props => props.theme.breakpoints['2xl']}) {
    font-size: ${props => {
      if (props.size === 'xl') return props.theme.typography.fontSize['2xl'];
      if (props.size === 'lg') return props.theme.typography.fontSize.xl;
      return props.theme.typography.fontSize.lg;
    }};
  }
  
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary.main};
    outline-offset: 2px;
  }
  
  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  
  /* Size variants */
  ${props => {
    switch (props.size) {
      case 'sm':
        return `
          height: 2.25rem;
          padding: 0 ${props.theme.spacing.md};
        `;
      case 'lg':
        return `
          height: 3rem;
          padding: 0 ${props.theme.spacing['2xl']};
          
          @media (min-width: ${props.theme.breakpoints.xl}) {
            height: 3.5rem;
            padding: 0 2.5rem;
          }
          
          @media (min-width: ${props.theme.breakpoints['2xl']}) {
            height: 4rem;
            padding: 0 3rem;
          }
        `;
      case 'xl':
        return `
          height: 3.5rem;
          padding: 0 2.5rem;
          
          @media (min-width: ${props.theme.breakpoints.xl}) {
            height: 4rem;
            padding: 0 3rem;
          }
          
          @media (min-width: ${props.theme.breakpoints['2xl']}) {
            height: 4.5rem;
            padding: 0 3.5rem;
          }
        `;
      default:
        return `
          height: 2.5rem;
          padding: 0 ${props.theme.spacing.lg};
        `;
    }
  }}
  
  /* Variant styles */
  ${props => {
    switch (props.variant) {
      case 'hero':
        return `
          background: ${props.theme.colors.background.card};
          color: ${props.theme.colors.primary.main};
          font-weight: ${props.theme.typography.fontWeight.semibold};
          box-shadow: ${props.theme.shadows.xl};
          border: none;
          
          &:hover {
            box-shadow: ${props.theme.shadows.xl}, 0 0 0 4px ${props.theme.colors.primary.main}20;
            transform: scale(1.05);
            background: ${props.theme.colors.background.card}95;
          }
        `;
      case 'heroInverse':
        return `
          background: ${props.theme.colors.primary.main};
          color: ${props.theme.colors.primary.foreground};
          font-weight: ${props.theme.typography.fontWeight.semibold};
          box-shadow: ${props.theme.shadows.xl};
          border: none;
          
          &:hover {
            box-shadow: ${props.theme.shadows.xl}, 0 0 0 4px ${props.theme.colors.primary.main}20;
            transform: scale(1.05);
            background: ${props.theme.colors.primary.dark};
          }
        `;
      case 'cta':
        return `
          background: linear-gradient(to right, ${props.theme.colors.accent.gold}, ${props.theme.colors.accent.goldDark});
          color: ${props.theme.colors.primary.foreground};
          font-weight: ${props.theme.typography.fontWeight.bold};
          box-shadow: ${props.theme.shadows.xl};
          border: none;
          
          &:hover {
            box-shadow: ${props.theme.shadows.xl}, 0 0 0 4px ${props.theme.colors.accent.gold}20;
            transform: scale(1.05);
          }
        `;
      case 'outline':
        return `
          background: ${props.theme.colors.background.card};
          color: ${props.theme.colors.text.primary};
          border: 1px solid ${props.theme.colors.border.main};
          
          &:hover {
            background: ${props.theme.colors.accent.main};
            color: ${props.theme.colors.accent.foreground};
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: ${props.theme.colors.text.primary};
          
          &:hover {
            background: ${props.theme.colors.accent.main};
            color: ${props.theme.colors.accent.foreground};
          }
        `;
      case 'link':
        return `
          background: transparent;
          color: ${props.theme.colors.primary.main};
          text-decoration: underline;
          text-underline-offset: 4px;
          padding: 0;
          height: auto;
          
          &:hover {
            color: ${props.theme.colors.primary.dark};
            opacity: 0.9;
          }
        `;
      default:
        return `
          background: ${props.theme.colors.primary.main};
          color: ${props.theme.colors.primary.foreground};
          
          &:hover {
            background: ${props.theme.colors.primary.dark};
          }
        `;
    }
  }}
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    pointer-events: none;
    flex-shrink: 0;
    
    @media (min-width: ${props => props.theme.breakpoints.xl}) {
      width: 1.5rem;
      height: 1.5rem;
    }
    
    @media (min-width: ${props => props.theme.breakpoints['2xl']}) {
      width: 1.75rem;
      height: 1.75rem;
    }
  }
`;

const Button = React.forwardRef(({ 
  children, 
  variant = 'default', 
  size = 'default',
  asChild,
  ...props 
}, ref) => {
  const Comp = asChild ? 'div' : StyledButton;
  
  return (
    <Comp
      ref={ref}
      variant={variant}
      size={size}
      {...props}
    >
      {children}
    </Comp>
  );
});

Button.displayName = 'Button';

export default Button;
